<template>
  <div
    class="position-relative search-admin-wrapper"
    v-click-outside="handleClickOutside"
    @click.capture="handleClickInside"
  >
    <mayday-web-search-input
      ref="super-search-input"
      class="mb-1 search-input"
      :tags="selectedTags"
      :focus-case-id="focusCaseId"
      v-model="searchInputFilter"
      @input="updateSearchQuery"
      @delete-tag="deleteTag"
      @delete-all="abortSearch"
      @focus-input="handleFocusInput"
      @activate-search-focus-case="handleSearchFocusCase"
    >
      <div class="px-3 d-flex">
        <font-awesome-icon
          v-if="searchInProgress"
          :icon="['fal', 'times']"
          class="icon-delete"
          @click="abortSearch"
        />
      </div>
      <font-awesome-icon
        :icon="['fad', 'sliders-v']"
        class="icon-filters"
        :class="{ 'text-blue': showFilters }"
        @click="handleShowFilters"
      />
    </mayday-web-search-input>
    <div class="search-filters is-dropdown">
      <SearchFilters
        ref="filters"
        v-if="showDropdown"
        :selected-tags="selectedTags"
        :searchInProgress="searchInProgress"
        :show-filters="showFilters"
        @update-selected-tags="updateTags"
        @close-dropdown="handleCloseDropdown"
        @activate-search-focus-case="handleSearchFocusCase"
      />
      <SearchRecentlyConsulted
        class="mt-1"
        v-if="displayRecentlyConsulted"
        isMaydayWeb
        :contents="orderedUserRecentlyConsultedContents(focusKnowledgeValue)"
        @go-to-document="
          goToWebTranslatedEntity({
            entityId: $event.id,
            entityType: $event.type,
            lang: $event.lang,
            query: { source: 'search' },
          })
        "
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash.debounce';

import MaydayWebSearchInput from './MaydayWebSearchInput';
import SearchFilters from './SearchFilters';
import SearchRecentlyConsulted from './SearchRecentlyConsulted';

export default {
  name: 'mayday-web-search-wrapper',
  components: {
    MaydayWebSearchInput,
    SearchFilters,
    SearchRecentlyConsulted,
  },
  data() {
    return {
      showDropdown: false,
      showFilters: false,
      searchInputFilter: '',
      selectedTags: [],
      debounceSearchFilters: debounce(() => {
        this.search();
      }, 200),
      searchInFocusCase: false,
    };
  },
  watch: {
    searchInputFilter() {
      this.handleSearch();
    },
    searchSize() {
      this.handleSearch();
    },
    selectedTags() {
      this.handleSearch();
    },
    searchInFocusCase() {
      this.handleSearch();
    },
    searchQuery() {
      this.searchInputFilter = this.searchQuery;
    },
    searchFilters() {
      this.selectedTags = this.searchFilters;
    },
    $route() {
      this.resetSearch();
    },
  },
  computed: {
    displayRecentlyConsulted() {
      return (
        this.showDropdown &&
        !this.searchInProgress &&
        this.orderedUserRecentlyConsultedContents(this.focusKnowledgeValue) &&
        this.orderedUserRecentlyConsultedContents(this.focusKnowledgeValue)
          .length
      );
    },
    entityType() {
      return this.preFilters.type || null;
    },
    labels() {
      return this.preFilters.categories || [];
    },
    focusCaseId() {
      return this.$route.params.caseId;
    },
    preFilters() {
      return this.selectedTags.reduce((acc, tag) => {
        if (tag.key !== 'entity') {
          if (acc[tag.key]) {
            acc[tag.key].push(tag.value);
          } else {
            acc[tag.key] = [tag.value];
          }
        }
        return acc;
      }, {});
    },
    searchInProgress() {
      return this.selectedTags.length > 0 || this.searchInputFilter !== '';
    },
    ...mapGetters(['orderedUserRecentlyConsultedContents']),
    ...mapGetters('webKnowledgeModule', [
      'searchQuery',
      'searchFilters',
      'searchSize',
      'focusKnowledgeValue',
    ]),
  },
  methods: {
    ...mapActions('webKnowledgeModule', [
      'maydayWebSearchAction',
      'updateSearchQuery',
      'updateSearchFilters',
      'updateSearchResults',
      'initSearch',
      'storeSearchAbortEvent',
      'updateSearchFocusLevel',
      'goToWebTranslatedEntity',
    ]),
    updateTags(tags) {
      this.selectedTags = tags;
      this.updateSearchFilters(tags);
    },
    deleteTag(tag) {
      let tags = [...this.selectedTags];
      const index = tags.findIndex((t) => t.id === tag.id && t.key === tag.key);
      tags.splice(index, 1);
      if (tag.key === 'entity') {
        tags = [];
      } else if (tag.key === 'knowledge') {
        tags = tags.filter((t) => t.key === 'entity');
      } else if (tag.key === 'family') {
        tags = tags.filter((t) => t.key === 'entity' || t.key === 'knowledge');
      }
      this.updateTags(tags);
    },
    resetSearch() {
      this.searchInputFilter = '';
      this.selectedTags = [];
      this.handleSearchFocusCase(false);
      this.initSearch();
    },
    abortSearch() {
      this.handleClickOutside();
      if (this.searchInProgress) this.storeSearchAbortEvent();
      this.resetSearch();
    },
    handleSearch() {
      if (this.searchInProgress) this.debounceSearchFilters();
      else {
        this.updateSearchResults({
          results: [],
          totalCount: 0,
        });
      }
    },
    async search() {
      try {
        await this.maydayWebSearchAction({
          query: this.searchInputFilter,
          entityType: this.entityType,
          categories: this.labels,
          caseId: this.focusCaseId,
        });
      } catch (e) {
        console.log(e);
      }
    },
    handleClickInside() {
      this.$refs['super-search-input'].focusInput();
    },
    handleClickOutside() {
      this.showDropdown = false;
      this.showFilters = false;
      this.$refs['super-search-input'].blurInput();
    },
    handleFocusInput() {
      this.showDropdown = true;
    },
    handleShowFilters() {
      this.showDropdown = true;
      this.showFilters = !this.showFilters;
    },
    handleCloseDropdown() {
      this.showDropdown = false;
      this.showFilters = false;
    },
    handleSearchFocusCase(e) {
      const level = e ? 'case' : '';
      this.updateSearchFocusLevel(level);
    },
  },
  mounted() {
    this.searchInputFilter = this.searchQuery;
    this.selectedTags = this.searchFilters;

    const isHome = this.$route.path === '/web';
    if (this.searchInProgress || isHome) this.handleClickInside();
    if (this.selectedTags.length) {
      this.showDropdown = true;
      this.$nextTick(() => {
        this.$refs.filters.initTags(this.selectedTags);
      });
    }
  },
  beforeDestroy() {
    this.debounceSearchFilters.flush();
  },
};
</script>

<style lang="scss" scoped>
.search-admin-wrapper {
  width: 100%;
}
.search-input {
  border: 1px solid $grey-3-mayday;
  border-radius: 4px;
  background: white;
  padding: 4px 8px;

  :deep() input {
    background: white;
    font-size: 18px;
    line-height: 40px;
    border: none;
  }

  :deep() input::placeholder {
    font-weight: 500;
    color: $grey-4-mayday;
  }

  :deep() .search-icon {
    padding-right: 4px;
    font-size: 18px;
    color: $grey-4-mayday;
    width: 24px;
  }
}
.search-filters {
  width: 100%;
  background: white;
  border-radius: 2px;
}
.is-dropdown {
  position: absolute;
  box-shadow: 0 15px 35px rgba(50, 50, 93, 0.2), 0 5px 15px rgba(0, 0, 0, 0.17);
  -webkit-box-shadow: 0 15px 35px rgba(50, 50, 93, 0.2),
    0 5px 15px rgba(0, 0, 0, 0.17);
  z-index: 20;
}
.icon-delete {
  font-size: 22px;
  color: $grey-5-mayday;
  cursor: pointer;
}
.icon-filters {
  font-size: 16px;
  color: $grey-6-mayday;
  cursor: pointer;
}
</style>
